/** @module @liquidefy/code-editor/ai/ai-cache @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  AICacheState,
  AICacheEntry,
  AICacheConfig,
  AICacheStats,
  AIRequest,
  AIResponse,
  CacheEntryId,
  EditorResult,
} from '../../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asCacheEntryId,
  now,
  DEFAULT_AI_CACHE_CONFIG,
} from '../../code-editor.js';

// ═══ §2 CACHE OPERATIONS — lookup, insert, invalidate, clear ═══

/**
 * Look up an exact cache entry by key.
 */
export function lookupCache(state: AICacheState, key: string): AICacheEntry | null {
  const entry = state.entries.get(key);
  if (!entry) return null;
  if (isExpired(entry)) return null;
  return entry;
}

/**
 * Look up a similar cache entry by fuzzy key matching.
 */
export function lookupSimilar(
  state: AICacheState,
  key: string,
  threshold: number = state.config.similarityThreshold,
): AICacheEntry | null {
  let bestMatch: AICacheEntry | null = null;
  let bestScore = 0;

  for (const [storedKey, entry] of state.entries) {
    if (isExpired(entry)) continue;
    const similarity = textSimilarity(key, storedKey);
    if (similarity >= threshold && similarity > bestScore) {
      bestScore = similarity;
      bestMatch = entry;
    }
  }

  return bestMatch;
}

/**
 * Insert a new entry into the cache. Evicts if full.
 */
export function insertCache(
  state: AICacheState,
  request: AIRequest,
  response: AIResponse,
  config?: Partial<AICacheConfig>,
): AICacheState {
  const mergedConfig = config ? { ...state.config, ...config } : state.config;
  let newState = state;

  // Evict if at capacity
  if (newState.entries.size >= mergedConfig.maxEntries) {
    newState = evictCache(newState, 1);
  }

  const key = computeCacheKey(request);
  const estimatedSize = estimateEntrySize(request, response);
  const ttl = computeTTL(response, mergedConfig);

  const entry: AICacheEntry = {
    id: asCacheEntryId(generateId('cache')),
    key,
    request,
    response,
    timestamp: now(),
    ttl,
    accessCount: 0,
    lastAccessed: now(),
    quality: response.confidence,
    size: estimatedSize,
  };

  const newEntries = new Map(newState.entries);
  newEntries.set(key, entry);

  return {
    ...newState,
    entries: newEntries,
    stats: updateStats(newState.stats, false, 0),
  };
}

/**
 * Invalidate a specific cache entry by key.
 */
export function invalidateCache(state: AICacheState, key: string): AICacheState {
  const newEntries = new Map(state.entries);
  newEntries.delete(key);
  return { ...state, entries: newEntries };
}

/**
 * Invalidate all entries matching a pattern (substring match on key).
 */
export function invalidateByPattern(state: AICacheState, pattern: string): AICacheState {
  const newEntries = new Map<string, AICacheEntry>();
  for (const [key, entry] of state.entries) {
    if (!key.includes(pattern)) {
      newEntries.set(key, entry);
    }
  }
  return { ...state, entries: newEntries };
}

/**
 * Clear the entire cache.
 */
export function clearCache(state: AICacheState): AICacheState {
  return {
    ...state,
    entries: new Map(),
    stats: resetStats(state.stats),
  };
}

/**
 * Current number of entries in the cache.
 */
export function cacheSize(state: AICacheState): number {
  return state.entries.size;
}

/**
 * Check if the cache is full.
 */
export function isCacheFull(state: AICacheState): boolean {
  return state.entries.size >= state.config.maxEntries;
}

// ═══ §3 CACHE KEY — deterministic hashing and normalization ═══

/**
 * Compute a deterministic cache key from a request.
 */
export function computeCacheKey(request: AIRequest): string {
  const normalized = normalizeForCaching(request);
  const components = keyComponents(normalized);
  const raw = `${components.module}:${components.type}:${components.contextHash}`;
  return simpleHash(raw);
}

/**
 * Normalize a request by stripping volatile fields (id, timestamp).
 */
export function normalizeForCaching(request: AIRequest): AIRequest {
  return {
    ...request,
    id: '' as AIRequest['id'],
    timestamp: 0,
  };
}

/**
 * Extract the key components from a request.
 */
export function keyComponents(request: AIRequest): {
  readonly module: string;
  readonly type: string;
  readonly contextHash: string;
} {
  const ctxParts: string[] = [];
  if (request.context.fileUri) ctxParts.push(request.context.fileUri);
  if (request.context.language) ctxParts.push(String(request.context.language));
  if (request.context.cursorPosition) {
    ctxParts.push(`${request.context.cursorPosition.line}:${request.context.cursorPosition.column}`);
  }
  if (request.context.symbols) ctxParts.push(request.context.symbols.join(','));

  return {
    module: request.module,
    type: request.type,
    contextHash: simpleHash(ctxParts.join('|')),
  };
}

/**
 * Exact key match check.
 */
export function isKeyMatch(stored: string, query: string): boolean {
  return stored === query;
}

/**
 * Fuzzy key match check.
 */
export function isSimilarKey(stored: string, query: string, threshold: number): boolean {
  return textSimilarity(stored, query) >= threshold;
}

function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    hash = ((hash << 5) - hash + ch) | 0;
  }
  return Math.abs(hash).toString(36);
}

function computeTTL(response: AIResponse, config: AICacheConfig): number {
  // Higher confidence responses get longer TTL
  const confidenceMultiplier = 0.5 + response.confidence * 1.5;
  // Analysis and conversation are more volatile
  const typeMultiplier = response.content.type === 'analysis' ? 0.5 : 1;
  return Math.round(config.defaultTtlMs * confidenceMultiplier * typeMultiplier);
}

function estimateEntrySize(request: AIRequest, response: AIResponse): number {
  let size = 200; // Base overhead
  if (response.content.value) {
    const val = response.content.value;
    if (typeof val === 'string') size += val.length;
    else {
      try { size += JSON.stringify(val).length; } catch { size += 500; }
    }
  }
  if (request.context.symbols) size += request.context.symbols.join(',').length;
  return size;
}

// ═══ §4 EVICTION — LRU, LFU, usefulness, TTL-based eviction ═══

/**
 * Default batch size for eviction.
 */
export const EVICTION_BATCH_SIZE = 10;

/**
 * Evict entries from the cache according to the configured policy.
 */
export function evictCache(state: AICacheState, count: number = EVICTION_BATCH_SIZE): AICacheState {
  switch (state.config.evictionPolicy) {
    case 'lru': return evictLRU(state, count);
    case 'lfu': return evictLFU(state, count);
    case 'usefulness': return evictByUsefulness(state, count);
    case 'ttl': return evictByTTL(state);
    default: return evictLRU(state, count);
  }
}

/**
 * Evict the least recently used entries.
 */
export function evictLRU(state: AICacheState, count: number = EVICTION_BATCH_SIZE): AICacheState {
  const entries = [...state.entries.entries()];
  entries.sort((a, b) => a[1].lastAccessed - b[1].lastAccessed);
  const toRemove = entries.slice(0, count).map(([key]) => key);
  return removeEntries(state, toRemove);
}

/**
 * Evict the least frequently used entries.
 */
export function evictLFU(state: AICacheState, count: number = EVICTION_BATCH_SIZE): AICacheState {
  const entries = [...state.entries.entries()];
  entries.sort((a, b) => a[1].accessCount - b[1].accessCount);
  const toRemove = entries.slice(0, count).map(([key]) => key);
  return removeEntries(state, toRemove);
}

/**
 * Evict entries by usefulness score (recency 40% + frequency 40% + quality 20%).
 */
export function evictByUsefulness(state: AICacheState, count: number = EVICTION_BATCH_SIZE): AICacheState {
  const currentTime = now();
  const entries = [...state.entries.entries()];

  // Compute usefulness scores
  const maxAccess = Math.max(1, ...entries.map(([, e]) => e.accessCount));
  const maxAge = Math.max(1, ...entries.map(([, e]) => currentTime - e.lastAccessed));

  const scored = entries.map(([key, entry]) => {
    const recency = 1 - ((currentTime - entry.lastAccessed) / maxAge);
    const frequency = entry.accessCount / maxAccess;
    const quality = entry.quality;
    const usefulness = recency * 0.4 + frequency * 0.4 + quality * 0.2;
    return { key, usefulness };
  });

  scored.sort((a, b) => a.usefulness - b.usefulness);
  const toRemove = scored.slice(0, count).map((s) => s.key);
  return removeEntries(state, toRemove);
}

/**
 * Evict all expired entries.
 */
export function evictByTTL(state: AICacheState): AICacheState {
  const toRemove: string[] = [];
  for (const [key, entry] of state.entries) {
    if (isExpired(entry)) toRemove.push(key);
  }
  return removeEntries(state, toRemove);
}

/**
 * Evict entries until cache size is below targetSizeMb.
 */
export function evictBySize(state: AICacheState, targetSizeMb: number): AICacheState {
  const targetBytes = targetSizeMb * 1024 * 1024;
  let currentSize = estimateMemoryUsage(state);
  if (currentSize <= targetBytes) return state;

  // Sort by usefulness, remove least useful first
  const entries = [...state.entries.entries()];
  entries.sort((a, b) => a[1].accessCount - b[1].accessCount);

  const newEntries = new Map(state.entries);
  for (const [key, entry] of entries) {
    if (currentSize <= targetBytes) break;
    currentSize -= entry.size;
    newEntries.delete(key);
  }

  return { ...state, entries: newEntries };
}

/**
 * Check if a cache entry has expired.
 */
export function isExpired(entry: AICacheEntry): boolean {
  return now() > entry.timestamp + entry.ttl;
}

/**
 * Check if an entry should be evicted based on config.
 */
export function shouldEvict(entry: AICacheEntry, config: AICacheConfig): boolean {
  if (isExpired(entry)) return true;
  if (entry.quality < 0.2 && entry.accessCount < 2) return true;
  return false;
}

function removeEntries(state: AICacheState, keys: readonly string[]): AICacheState {
  const newEntries = new Map(state.entries);
  for (const key of keys) {
    newEntries.delete(key);
  }
  return {
    ...state,
    entries: newEntries,
    stats: {
      ...state.stats,
      evictionCount: state.stats.evictionCount + keys.length,
      cacheSize: newEntries.size,
    },
  };
}

// ═══ §5 SIMILARITY — text and context similarity for fuzzy cache matching ═══

/**
 * Similarity weights for different components.
 */
export const SIMILARITY_WEIGHTS = {
  module: 0.2,
  type: 0.2,
  context: 0.4,
  constraints: 0.2,
} as const;

/**
 * Compute Jaccard similarity between two strings using word tokens.
 */
export function textSimilarity(a: string, b: string): number {
  const setA = new Set(a.toLowerCase().split(/\W+/).filter(Boolean));
  const setB = new Set(b.toLowerCase().split(/\W+/).filter(Boolean));
  return jaccardCoefficient(setA, setB);
}

/**
 * Compare two AIContext objects with weighted field matching.
 */
export function contextSimilarity(
  a: { readonly fileUri?: string; readonly language?: string; readonly symbols?: readonly string[] },
  b: { readonly fileUri?: string; readonly language?: string; readonly symbols?: readonly string[] },
): number {
  let score = 0;
  let weights = 0;

  // File URI match
  if (a.fileUri && b.fileUri) {
    score += (a.fileUri === b.fileUri ? 1 : 0) * 0.4;
    weights += 0.4;
  }

  // Language match
  if (a.language && b.language) {
    score += (a.language === b.language ? 1 : 0) * 0.2;
    weights += 0.2;
  }

  // Symbol overlap
  if (a.symbols && b.symbols && a.symbols.length > 0 && b.symbols.length > 0) {
    const setA = new Set(a.symbols);
    const setB = new Set(b.symbols);
    score += jaccardCoefficient(setA, setB) * 0.4;
    weights += 0.4;
  }

  return weights > 0 ? score / weights : 0;
}

/**
 * Composite similarity between two AIRequests.
 */
export function requestSimilarity(a: AIRequest, b: AIRequest): number {
  let score = 0;

  // Module match
  score += (a.module === b.module ? 1 : 0) * SIMILARITY_WEIGHTS.module;

  // Type match
  score += (a.type === b.type ? 1 : 0) * SIMILARITY_WEIGHTS.type;

  // Context similarity
  score += contextSimilarity(a.context, b.context) * SIMILARITY_WEIGHTS.context;

  // Constraints similarity
  const constraintsMatch = constraintsSimilarity(a.constraints, b.constraints);
  score += constraintsMatch * SIMILARITY_WEIGHTS.constraints;

  return score;
}

function constraintsSimilarity(
  a?: { readonly maxTokens?: number; readonly maxLatencyMs?: number },
  b?: { readonly maxTokens?: number; readonly maxLatencyMs?: number },
): number {
  if (!a && !b) return 1;
  if (!a || !b) return 0.5;
  let matches = 0;
  let comparisons = 0;
  if (a.maxTokens !== undefined && b.maxTokens !== undefined) {
    const ratio = Math.min(a.maxTokens, b.maxTokens) / Math.max(a.maxTokens, b.maxTokens);
    matches += ratio;
    comparisons++;
  }
  if (a.maxLatencyMs !== undefined && b.maxLatencyMs !== undefined) {
    const ratio = Math.min(a.maxLatencyMs, b.maxLatencyMs) / Math.max(a.maxLatencyMs, b.maxLatencyMs);
    matches += ratio;
    comparisons++;
  }
  return comparisons > 0 ? matches / comparisons : 0.5;
}

/**
 * Generate trigram set from text.
 */
export function trigrams(text: string): Set<string> {
  const result = new Set<string>();
  const lower = text.toLowerCase();
  for (let i = 0; i <= lower.length - 3; i++) {
    result.add(lower.slice(i, i + 3));
  }
  return result;
}

/**
 * Compute the Jaccard coefficient between two sets.
 */
export function jaccardCoefficient<T>(setA: ReadonlySet<T>, setB: ReadonlySet<T>): number {
  if (setA.size === 0 && setB.size === 0) return 1;

  let intersection = 0;
  for (const item of setA) {
    if (setB.has(item)) intersection++;
  }

  const union = setA.size + setB.size - intersection;
  return union > 0 ? intersection / union : 0;
}

// ═══ §6 CACHE STATISTICS — hit rate, efficiency, reporting ═══

/**
 * Update cache stats with a hit or miss.
 */
export function updateStats(stats: AICacheStats, hit: boolean, responseTimeMs: number): AICacheStats {
  const totalRequests = stats.totalRequests + 1;
  const totalHits = stats.totalHits + (hit ? 1 : 0);
  const totalMisses = stats.totalMisses + (hit ? 0 : 1);
  const hitRate = totalRequests > 0 ? totalHits / totalRequests : 0;
  const missRate = totalRequests > 0 ? totalMisses / totalRequests : 0;

  // Running average response time
  const avgResponseTime = stats.totalRequests === 0
    ? responseTimeMs
    : (stats.averageResponseTimeMs * stats.totalRequests + responseTimeMs) / totalRequests;

  return {
    ...stats,
    totalRequests,
    totalHits,
    totalMisses,
    hitRate,
    missRate,
    averageResponseTimeMs: Math.round(avgResponseTime),
  };
}

/**
 * Cache hit rate.
 */
export function hitRate(stats: AICacheStats): number {
  return stats.hitRate;
}

/**
 * Cache miss rate.
 */
export function missRate(stats: AICacheStats): number {
  return stats.missRate;
}

/**
 * Average response time in milliseconds.
 */
export function averageResponseTime(stats: AICacheStats): number {
  return stats.averageResponseTimeMs;
}

/**
 * Estimate time saved by caching.
 */
export function cacheEfficiency(stats: AICacheStats): number {
  // Assume average non-cached response is 2000ms
  const estimatedSavedMs = stats.totalHits * 2000;
  return estimatedSavedMs;
}

/**
 * Reset all stats to zero.
 */
export function resetStats(stats: AICacheStats): AICacheStats {
  return {
    hitRate: 0,
    missRate: 0,
    totalHits: 0,
    totalMisses: 0,
    totalRequests: 0,
    averageResponseTimeMs: 0,
    cacheSize: 0,
    evictionCount: 0,
  };
}

/**
 * Generate a human-readable stats report.
 */
export function statsReport(stats: AICacheStats): string {
  const lines: string[] = [];
  lines.push('=== Cache Statistics ===');
  lines.push(`Total requests: ${stats.totalRequests}`);
  lines.push(`Hits: ${stats.totalHits} (${(stats.hitRate * 100).toFixed(1)}%)`);
  lines.push(`Misses: ${stats.totalMisses} (${(stats.missRate * 100).toFixed(1)}%)`);
  lines.push(`Avg response time: ${stats.averageResponseTimeMs}ms`);
  lines.push(`Cache size: ${stats.cacheSize} entries`);
  lines.push(`Evictions: ${stats.evictionCount}`);
  lines.push(`Est. time saved: ${cacheEfficiency(stats)}ms`);
  return lines.join('\n');
}

/**
 * Estimate total memory usage of the cache in bytes.
 */
export function estimateMemoryUsage(state: AICacheState): number {
  let total = 0;
  for (const [, entry] of state.entries) {
    total += entry.size;
  }
  return total;
}
